import { useEffect, useState } from "react";
import { ChevronDown, MailOpen, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { inputCls } from "./fields";

const STATUS_STYLES = {
  new: "text-gold-300 border-gold-400/40 bg-gold-400/10",
  in_progress: "text-sky-300 border-sky-400/40 bg-sky-400/10",
  closed: "text-slate-400 border-white/15 bg-white/5",
};

const fmtDateTime = (d) => {
  try {
    return new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return d;
  }
};

export const BespokeInbox = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = () => {
    api.get(`/admin/enquiries/bespoke${filter ? `?status=${filter}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, [filter]);

  const visible = filter ? items : items.filter((e) => e.status !== "closed");

  const save = async (id) => {
    try {
      await api.patch(`/admin/enquiries/bespoke/${id}`, drafts[id] || {});
      toast.success("Enquiry updated");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="admin-bespoke-inbox">
      <h1 className="font-serif text-2xl text-slate-50 mb-6">Bespoke Enquiries</h1>
      <div className="flex gap-2 mb-6 flex-wrap">
        {[["", "Active"], ["new", "New"], ["in_progress", "In Progress"], ["closed", "Closed"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v || "all"}`}
            className={`text-[11px] tracking-[0.15em] uppercase rounded-full px-4 py-2 border transition-colors ${
              filter === v ? "border-gold-400/60 text-gold-200 bg-gold-400/10" : "border-white/10 text-slate-400 hover:text-gold-200"
            }`}>
            {l}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {visible.map((e) => {
          const open = openId === e.id;
          const draft = drafts[e.id] || { status: e.status, notes: e.notes };
          return (
            <div key={e.id} className="rounded-xl border border-white/5 bg-navy-900/50" data-testid={`enquiry-row-${e.id}`}>
              <button onClick={() => setOpenId(open ? null : e.id)} className="w-full flex items-center gap-4 px-5 py-4 text-left"
                data-testid={`enquiry-expand-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-100 truncate">
                    {e.name} — {e.occasion} · <span className="capitalize">{e.package}</span> · £{e.total_quoted}
                  </p>
                  <p className="text-[11px] text-slate-500">{fmtDateTime(e.created_at)} · {e.email}</p>
                </div>
                <span className={`text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`}>
                  {e.status.replace("_", " ")}
                </span>
                <ChevronDown size={16} className={`text-gold-400 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="px-5 pb-5 pt-1 border-t border-white/5 space-y-3 text-sm" data-testid={`enquiry-detail-${e.id}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-300 font-light">
                    <p><span className="text-slate-500">Phone:</span> {e.phone || "—"}</p>
                    <p><span className="text-slate-500">Preferred contact:</span> {e.contact_pref}</p>
                    <p className="sm:col-span-2"><span className="text-slate-500">Extras:</span> {e.extras?.length ? e.extras.join(", ") : "None"}</p>
                  </div>
                  <p className="text-slate-300 font-light whitespace-pre-line border-l-2 border-gold-400/40 pl-4">{e.message}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <select className={inputCls} value={draft.status}
                      onChange={(ev) => setDrafts({ ...drafts, [e.id]: { ...draft, status: ev.target.value } })}
                      data-testid={`enquiry-status-${e.id}`}>
                      <option value="new" className="bg-navy-900">New</option>
                      <option value="in_progress" className="bg-navy-900">In Progress</option>
                      <option value="closed" className="bg-navy-900">Closed</option>
                    </select>
                    <input className={inputCls} placeholder="Internal notes…" value={draft.notes || ""}
                      onChange={(ev) => setDrafts({ ...drafts, [e.id]: { ...draft, notes: ev.target.value } })}
                      data-testid={`enquiry-notes-${e.id}`} />
                  </div>
                  <button onClick={() => save(e.id)} className="btn-gold !px-5 !py-2 text-xs" data-testid={`enquiry-save-${e.id}`}>Save</button>
                </div>
              )}
            </div>
          );
        })}
        {visible.length === 0 && <p className="text-sm text-slate-500">No enquiries here yet.</p>}
      </div>
    </div>
  );
};

export const ContactInbox = () => {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [folder, setFolder] = useState("inbox");

  const load = () => {
    api.get("/admin/enquiries/contact").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (e) => {
    await api.patch(`/admin/enquiries/contact/${e.id}`, { read: !e.read });
    load();
  };

  const shown = items.filter((e) => (folder === "inbox" ? !e.read : e.read));
  const unread = items.filter((e) => !e.read).length;

  return (
    <div data-testid="admin-contact-inbox">
      <h1 className="font-serif text-2xl text-slate-50 mb-6">Contact Inbox</h1>
      <div className="flex gap-2 mb-6 flex-wrap">
        {[["inbox", `Inbox (${unread})`], ["done", `Dealt With (${items.length - unread})`]].map(([v, l]) => (
          <button key={v} onClick={() => setFolder(v)} data-testid={`contact-folder-${v}`}
            className={`text-[11px] tracking-[0.15em] uppercase rounded-full px-4 py-2 border transition-colors ${
              folder === v ? "border-gold-400/60 text-gold-200 bg-gold-400/10" : "border-white/10 text-slate-400 hover:text-gold-200"
            }`}>
            {l}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {shown.map((e) => {
          const open = openId === e.id;
          return (
            <div key={e.id} className={`rounded-xl border ${e.read ? "border-white/5 bg-navy-900/40" : "border-gold-400/30 bg-navy-900/60"}`} data-testid={`contact-row-${e.id}`}>
              <button onClick={() => setOpenId(open ? null : e.id)} className="w-full flex items-center gap-4 px-5 py-4 text-left"
                data-testid={`contact-expand-${e.id}`}>
                {e.read ? <MailOpen size={15} className="text-slate-500 shrink-0" /> : <Mail size={15} className="text-gold-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${e.read ? "text-slate-400" : "text-slate-100"}`}>{e.name}</p>
                  <p className="text-[11px] text-slate-500">{fmtDateTime(e.created_at)} · {e.email}</p>
                </div>
                <ChevronDown size={16} className={`text-gold-400 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="px-5 pb-5 pt-1 border-t border-white/5 space-y-4" data-testid={`contact-detail-${e.id}`}>
                  <p className="text-sm text-slate-300 font-light whitespace-pre-line border-l-2 border-gold-400/40 pl-4">{e.message}</p>
                  <div className="flex gap-3">
                    <a href={`mailto:${e.email}`} className="btn-ghost-gold !px-5 !py-2 text-xs" data-testid={`contact-reply-${e.id}`}>Reply by email</a>
                    <button onClick={() => toggleRead(e)} className="text-xs text-slate-400 hover:text-gold-200 border border-white/10 rounded-full px-4 py-2"
                      data-testid={`contact-read-toggle-${e.id}`}>
                      Mark as {e.read ? "unread" : "read"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && (
          <p className="text-sm text-slate-500">
            {folder === "inbox" ? "Inbox zero — nothing waiting." : "Nothing dealt with yet."}
          </p>
        )}
      </div>
    </div>
  );
};
